import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Marquee from "react-fast-marquee";
import {
  TrendingUp,
  Check,
  ArrowRight,
  Shield,
  BarChart3,
  Clock,
  Award,
  Star,
  Zap,
  Users,
  Lock,
  ChevronRight,
  Crown,
} from "lucide-react";
import { FaBitcoin, FaEthereum } from "react-icons/fa";

const PackagesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [cryptoPrices, setCryptoPrices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [investDialogOpen, setInvestDialogOpen] = useState(false);
  const [investAmount, setInvestAmount] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricesRes, packagesRes] = await Promise.all([
          axios.get(`${API}/crypto/prices`),
          axios.get(`${API}/packages`),
        ]);
        setCryptoPrices(pricesRes.data?.slice(0, 10) || []);
        setPackages(packagesRes.data || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
  }, []);

  const getCryptoIcon = (symbol) => {
    if (symbol?.toLowerCase() === "btc") return <FaBitcoin className="w-4 h-4 text-amber-500" />;
    if (symbol?.toLowerCase() === "eth") return <FaEthereum className="w-4 h-4 text-blue-400" />;
    return <TrendingUp className="w-4 h-4 text-slate-400" />;
  };

  const handleSelectPackage = (pkg) => {
    if (!isAuthenticated) {
      // Store selected package in session storage for after login
      sessionStorage.setItem("selectedPackage", JSON.stringify(pkg));
      navigate("/register", { state: { fromPackage: pkg.package_id } });
    } else {
      setSelectedPackage(pkg);
      setInvestAmount(pkg.min_investment.toString());
      setInvestDialogOpen(true);
    }
  };

  const handleInvest = async () => {
    if (!investAmount || parseFloat(investAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (parseFloat(investAmount) < selectedPackage.min_investment) {
      toast.error(`Minimum investment is $${selectedPackage.min_investment.toLocaleString()}`);
      return;
    }

    if (parseFloat(investAmount) > selectedPackage.max_investment) {
      toast.error(`Maximum investment is $${selectedPackage.max_investment.toLocaleString()}`);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/investments`,
        { package_id: selectedPackage.package_id, amount: parseFloat(investAmount) },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Investment created successfully!");
      setInvestDialogOpen(false);
      setSelectedPackage(null);
      setInvestAmount("");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Investment failed. Please check your balance.");
    } finally {
      setLoading(false);
    }
  };

  const getTierConfig = (tier) => {
    const configs = {
      silver: {
        gradient: "from-slate-100 to-slate-200",
        border: "border-slate-300",
        iconBg: "bg-slate-100",
        iconColor: "text-slate-600",
        badge: null,
        buttonClass: "bg-navy-600 hover:bg-navy-700 text-white",
        checkColor: "text-slate-600",
        accentColor: "text-slate-700",
      },
      gold: {
        gradient: "from-amber-50 to-yellow-100",
        border: "border-amber-300",
        iconBg: "bg-amber-100",
        iconColor: "text-amber-600",
        badge: "Most Popular",
        badgeClass: "bg-amber-500 text-white",
        buttonClass: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg shadow-amber-500/30",
        checkColor: "text-amber-600",
        accentColor: "text-amber-700",
      },
      platinum: {
        gradient: "from-navy-600 to-navy-800",
        border: "border-navy-400",
        iconBg: "bg-navy-500",
        iconColor: "text-white",
        badge: "Best Value",
        badgeClass: "bg-gradient-to-r from-amber-400 to-amber-500 text-navy-900",
        buttonClass: "bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-navy-900 font-semibold shadow-lg shadow-amber-500/30",
        checkColor: "text-amber-400",
        accentColor: "text-amber-400",
        textColor: "text-white",
        subTextColor: "text-slate-300",
      },
    };
    return configs[tier] || configs.silver;
  };

  const getIcon = (tier) => {
    switch (tier) {
      case "silver": return <Shield className="w-8 h-8" />;
      case "gold": return <Award className="w-8 h-8" />;
      case "platinum": return <Crown className="w-8 h-8" />;
      default: return <Shield className="w-8 h-8" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Price Ticker */}
      <div className="bg-navy-600 border-b border-navy-500">
        <Marquee gradient={false} speed={40} className="py-2">
          <div className="flex gap-8 px-4">
            {cryptoPrices.map((coin) => (
              <div key={coin.id} className="flex items-center gap-2">
                {getCryptoIcon(coin.symbol)}
                <span className="font-medium text-slate-300 uppercase text-sm">{coin.symbol}</span>
                <span className="font-mono text-white text-sm">${coin.current_price?.toLocaleString()}</span>
                <span className={`font-mono text-xs ${coin.price_change_percentage_24h >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {coin.price_change_percentage_24h >= 0 ? "+" : ""}{coin.price_change_percentage_24h?.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </Marquee>
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-xl font-bold text-navy-600">AndInvestTrading</span>
            </Link>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard">
                    <Button variant="ghost" className="text-navy-600 hover:text-navy-700 hover:bg-navy-50">
                      Dashboard
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button className="btn-gold">
                      My Investments
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-navy-600 hover:text-navy-700 hover:bg-navy-50">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="btn-gold">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=2000&q=80" 
            alt="Investment background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-800/85 to-navy-900/90" />
        </div>
        
        {/* Animated overlay elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-72 h-72 bg-amber-400/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Star className="w-4 h-4 text-amber-400" />
            <span className="text-white/90 text-sm font-medium">Trusted by 15,000+ Investors</span>
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6">
            Choose Your Investment
            <span className="block text-amber-400">Package</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            Start building your wealth with our professionally managed investment packages. 
            Transparent returns, secure investments, and 24/7 support.
          </p>
          <div className="flex items-center justify-center gap-8 text-sm text-white/80">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-emerald-400" />
              <span>Bank-Level Security</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              <span>Instant Processing</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" />
              <span>Expert Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 -mt-8 relative z-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages
              .sort((a, b) => {
                const order = { silver: 1, gold: 2, platinum: 3 };
                return order[a.tier] - order[b.tier];
              })
              .map((pkg) => {
                const config = getTierConfig(pkg.tier);
                const isPlatinum = pkg.tier === "platinum";
                
                return (
                  <Card
                    key={pkg.package_id}
                    className={`relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                      isPlatinum 
                        ? "bg-gradient-to-br from-navy-600 to-navy-800 border-2 border-amber-400/50 shadow-xl shadow-navy-600/20" 
                        : `bg-gradient-to-br ${config.gradient} border-2 ${config.border} shadow-lg`
                    }`}
                    data-testid={`package-card-${pkg.tier}`}
                  >
                    {config.badge && (
                      <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${config.badgeClass}`}>
                        {config.badge}
                      </div>
                    )}
                    
                    <CardContent className="p-8">
                      {/* Icon & Title */}
                      <div className="mb-6">
                        <div className={`w-16 h-16 ${config.iconBg} rounded-2xl flex items-center justify-center mb-4 ${config.iconColor}`}>
                          {getIcon(pkg.tier)}
                        </div>
                        <h3 className={`font-heading text-2xl font-bold mb-1 ${isPlatinum ? "text-white" : "text-navy-700"}`}>
                          {pkg.name}
                        </h3>
                        <p className={`text-sm ${isPlatinum ? "text-slate-300" : "text-slate-500"}`}>
                          {pkg.duration_days} days investment period
                        </p>
                      </div>

                      {/* ROI */}
                      <div className="mb-6 pb-6 border-b border-slate-200/20">
                        <div className="flex items-baseline gap-2">
                          <span className={`text-5xl font-bold font-mono ${config.accentColor}`}>
                            {pkg.roi_percentage}%
                          </span>
                          <span className={`text-lg ${isPlatinum ? "text-slate-300" : "text-slate-500"}`}>ROI</span>
                        </div>
                        <p className={`mt-2 text-sm ${isPlatinum ? "text-slate-400" : "text-slate-500"}`}>
                          Guaranteed returns on your investment
                        </p>
                      </div>

                      {/* Investment Range */}
                      <div className="mb-6">
                        <div className={`flex justify-between text-sm mb-2 ${isPlatinum ? "text-slate-300" : "text-slate-600"}`}>
                          <span>Min Investment</span>
                          <span className="font-mono font-semibold">${pkg.min_investment.toLocaleString()}</span>
                        </div>
                        <div className={`flex justify-between text-sm ${isPlatinum ? "text-slate-300" : "text-slate-600"}`}>
                          <span>Max Investment</span>
                          <span className="font-mono font-semibold">${pkg.max_investment.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Features */}
                      <ul className="space-y-3 mb-8">
                        {pkg.features?.slice(0, 5).map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${config.checkColor}`} />
                            <span className={`text-sm ${isPlatinum ? "text-slate-200" : "text-slate-600"}`}>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA Button */}
                      <Button
                        className={`w-full py-6 text-base font-semibold ${config.buttonClass}`}
                        onClick={() => handleSelectPackage(pkg)}
                        data-testid={`select-package-${pkg.tier}`}
                      >
                        {isAuthenticated ? "Invest Now" : "Get Started"}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-navy-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-700 mb-4">
              Why Choose AndInvestTrading?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We provide a secure, transparent, and profitable investment experience
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Shield className="w-6 h-6" />, title: "Secure Platform", desc: "Bank-level encryption protects your investments" },
              { icon: <Zap className="w-6 h-6" />, title: "Fast Processing", desc: "Instant deposits and quick withdrawals" },
              { icon: <BarChart3 className="w-6 h-6" />, title: "Guaranteed ROI", desc: "Fixed returns as promised in each package" },
              { icon: <Users className="w-6 h-6" />, title: "24/7 Support", desc: "Expert team available round the clock" },
            ].map((benefit, i) => (
              <Card key={i} className="bg-white border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-navy-100 rounded-xl flex items-center justify-center mx-auto mb-4 text-navy-600">
                    {benefit.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-navy-700 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-500">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy-700 mb-4">
              How It Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Start investing in just 3 simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { step: "01", title: "Create Account", desc: "Sign up with your email and complete verification" },
              { step: "02", title: "Deposit Funds", desc: "Add funds via Bitcoin or USDT (TRC20)" },
              { step: "03", title: "Start Investing", desc: "Choose a package and watch your money grow" },
            ].map((item, i) => (
              <div key={i} className="text-center relative">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-navy-700 mb-2">{item.title}</h3>
                <p className="text-slate-500">{item.desc}</p>
                {i < 2 && (
                  <ChevronRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-slate-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1620321023374-d1a68fbc720d?auto=format&fit=crop&w=2000&q=80" 
            alt="Crypto background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 to-navy-800/90" />
        </div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Start Growing Your Wealth?
          </h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-8">
            Join thousands of successful investors. Create your free account today and start earning.
          </p>
          <Link to="/register">
            <Button size="lg" className="btn-gold text-lg px-8 py-6">
              Create Free Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-navy-800 border-t border-navy-700">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white">AndInvestTrading</span>
            </div>
            <p className="text-sm text-slate-400">
              &copy; 2026 AndInvestTrading. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Investment Dialog - For logged in users */}
      <Dialog open={investDialogOpen} onOpenChange={setInvestDialogOpen}>
        <DialogContent className="bg-white border-navy-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-navy-700 font-heading text-xl">
              Invest in {selectedPackage?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            {selectedPackage && (
              <>
                {/* Package Summary */}
                <div className="bg-navy-50 p-4 rounded-xl border border-navy-100">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-slate-600">Expected ROI</span>
                    <span className="text-2xl font-bold text-amber-600">{selectedPackage.roi_percentage}%</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500">Duration</span>
                    <span className="text-navy-600 font-medium">{selectedPackage.duration_days} days</span>
                  </div>
                </div>

                {/* Current Balance */}
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Your Balance</span>
                  <span className="font-mono font-semibold text-navy-700">${(user?.balance || 0).toLocaleString()}</span>
                </div>

                {/* Amount Input */}
                <div className="space-y-2">
                  <Label className="text-slate-700">Investment Amount (USD)</Label>
                  <Input
                    type="number"
                    placeholder={`Min: $${selectedPackage.min_investment.toLocaleString()}`}
                    value={investAmount}
                    onChange={(e) => setInvestAmount(e.target.value)}
                    className="border-navy-200 text-lg font-mono"
                    data-testid="invest-amount-input"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Min: ${selectedPackage.min_investment.toLocaleString()}</span>
                    <span>Max: ${selectedPackage.max_investment.toLocaleString()}</span>
                  </div>
                </div>

                {/* Expected Return Preview */}
                {investAmount && parseFloat(investAmount) > 0 && (
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                    <div className="flex justify-between items-center">
                      <span className="text-emerald-700">Expected Return</span>
                      <span className="text-xl font-bold text-emerald-600 font-mono">
                        ${(parseFloat(investAmount) * (1 + selectedPackage.roi_percentage / 100)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className="text-xs text-emerald-600 mt-1">
                      Profit: +${(parseFloat(investAmount) * selectedPackage.roi_percentage / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}

                {/* Insufficient Balance Warning */}
                {user?.balance < parseFloat(investAmount || 0) && (
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <p className="text-amber-700 text-sm">
                      Insufficient balance. <Link to="/dashboard" className="underline font-semibold">Deposit funds</Link> to continue.
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  onClick={handleInvest} 
                  className="w-full btn-gold py-6 text-base"
                  disabled={loading || (user?.balance < parseFloat(investAmount || 0))}
                  data-testid="confirm-invest-btn"
                >
                  {loading ? "Processing..." : "Confirm Investment"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesPage;
